import { useState, useMemo } from "react";

import Link from "next/link";

import { ProseMirror, ProseMirrorDoc } from "@handlewithcare/react-prosemirror";
import { Button, ChevronUpSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { EditorState } from "prosemirror-state";

import {
  LessonSparkAttachment,
  PMJSONNode,
  Standard,
} from "@/bundles/types/LessonSparkAttachment";
import useEditorStateFromJSON from "@/bundles/hooks/useEditorStateFromJSON";
import parseBodyIntoSections, {
  createDocFromNodes,
} from "@/bundles/utils/parseBodyIntoSections";

import {
  titleCss,
  fullPageLinkCss,
  sectionCss,
  collapsibleSectionCss,
  sectionToggleButtonCss,
  chevronCss,
  sectionBodyCss,
  standardItemCss,
  standardCodeCss,
  standardDescriptionCss,
  subheadingCss,
} from "./AttachmentItem.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface AttachmentItemProps {
  selectedResource: LessonSparkAttachment;
}

const ReadOnlyProseMirror = ({ state }: { state: EditorState }) => (
  <ProseMirror editable={() => false} state={state}>
    <ProseMirrorDoc />
  </ProseMirror>
);

type ContentSegment =
  | { type: "subheading"; text: string }
  | { type: "content"; nodes: PMJSONNode[] };

function segmentBySubheadings(contentNodes: PMJSONNode[]): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let currentNodes: PMJSONNode[] = [];

  for (const node of contentNodes) {
    if (node.type === "headings_block" && node.attrs?.["level"] === 3) {
      if (currentNodes.length > 0) {
        segments.push({ type: "content", nodes: currentNodes });
        currentNodes = [];
      }
      const text = node.content?.map((n) => n.text ?? "").join("") ?? "";
      segments.push({ type: "subheading", text });
    } else {
      currentNodes.push(node);
    }
  }

  if (currentNodes.length > 0) {
    segments.push({ type: "content", nodes: currentNodes });
  }

  return segments;
}

const ProseMirrorContent = ({
  contentNodes,
}: {
  contentNodes: PMJSONNode[];
}) => {
  const doc = useMemo(() => createDocFromNodes(contentNodes), [contentNodes]);
  const state = useEditorStateFromJSON(doc);
  if (!state) return null;
  return <ReadOnlyProseMirror state={state} />;
};

const SectionContent = ({ contentNodes }: { contentNodes: PMJSONNode[] }) => {
  const segments = useMemo(
    () => segmentBySubheadings(contentNodes),
    [contentNodes],
  );
  return (
    <>
      {segments.map((segment, i) =>
        segment.type === "subheading" ? (
          <div key={i} css={subheadingCss}>
            {segment.text}
          </div>
        ) : (
          <ProseMirrorContent key={i} contentNodes={segment.nodes} />
        ),
      )}
    </>
  );
};

const CollapsibleSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div css={collapsibleSectionCss}>
      <ButtonWithTrackEvent
        flavor={Button.flavors.incognito}
        ariaProps={{ "aria-expanded": isOpen }}
        dataProps={{
          "data-qa-selector": "toggle-lesson-spark-section-bundle-page",
        }}
        __cssFor={{ root: sectionToggleButtonCss }}
        onClick={() => setIsOpen((prev) => !prev)}
        actionName={actionNames.interact}
        objectType={objectTypes.content}
        streamProperties={{
          description: `Toggle lesson spark section "${title}" on bundle resource panel`,
        }}
      >
        <span>{title}</span>
        <ChevronUpSVG css={chevronCss(isOpen)} aria-hidden />
      </ButtonWithTrackEvent>
      {isOpen && <div css={sectionBodyCss}>{children}</div>}
    </div>
  );
};

const AttachmentItem = ({ selectedResource }: AttachmentItemProps) => {
  const titleState = useEditorStateFromJSON(
    selectedResource?.metadata?.raw_title,
  );

  const { preamble, sections } = useMemo(() => {
    if (!selectedResource?.metadata?.raw_body)
      return { preamble: [], sections: [] };
    return parseBodyIntoSections(selectedResource.metadata.raw_body);
  }, [selectedResource?.metadata?.raw_body]);

  const standards: Standard[] = selectedResource?.metadata?.standards ?? [];

  if (!selectedResource) return null;

  return (
    <div css={sectionCss}>
      {titleState && (
        <div css={titleCss}>
          <ReadOnlyProseMirror state={titleState} />
        </div>
      )}

      <Link
        css={fullPageLinkCss}
        target="_blank"
        rel="noopener nofollow noreferrer"
        aria-label="Open full page view (opens in new tab)"
        href={`/lesson-sparks/${selectedResource.id}`}
      >
        Open full page view
      </Link>

      {preamble.length > 0 && <SectionContent contentNodes={preamble} />}

      {sections.map((section, index) => (
        <CollapsibleSection key={index} title={section.heading}>
          <SectionContent contentNodes={section.contentNodes} />
        </CollapsibleSection>
      ))}

      {standards.length > 0 && (
        <CollapsibleSection title="Aligned Standards">
          {standards.map((standard) => (
            <div key={standard.id} css={standardItemCss}>
              <span css={standardCodeCss}>
                {standard.number_prefix_enhanced}
              </span>
              <p css={standardDescriptionCss}>{standard.description}</p>
            </div>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
};

export default AttachmentItem;
